import { useContext, useState } from 'react';
import { storeImagesQuery } from '../../graphql/queries';
import { uploadToS3 } from '@/utils/imageUpload';
import { useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { requestUploadUrl, updateStoreImages } from '../../graphql/mutations';
import { randomId } from '@/utils/random';
import { IconButton } from '../../components/Button/Button';
import {
  FaChevronLeft,
  FaChevronRight,
  FaSave,
  FaTrash,
  FaUpload,
} from 'react-icons/all';
import i18n from '@/utils/i18n';
import { Carousel } from 'primereact/carousel';
import { Button } from 'primereact/button';
import { getCurrentDatePlusDays } from '@/utils/date';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';

type StoreImageCarouselViewProps = {
  pickpackStoreId: string | null;
};

enum ImageSource {
  GoogleGqlType = 'GOOGLE',
  UploadByStoreGqlType = 'UPLOAD_BY_STORE',
  UploadByVytalGqlType = 'UPLOAD_BY_VYTAL',
}

interface Image {
  created_at?: string;
  source?: ImageSource;
  url?: string;
  original_url?: string;
  origin_url?: string;
}

type ImageWithIndex = Image & {
  index: number;
};

export const StoreImageCarouselView = ({
  pickpackStoreId,
}: StoreImageCarouselViewProps) => {
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const [dbImages, setDbImages] = useState<ImageWithIndex[]>([]);
  const [images, setImages] = useState<ImageWithIndex[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);
  const [updateStoreImagesMutation, { loading: updatingStoreImages }] =
    useMutation(updateStoreImages);
  // @ts-ignore
  const { loading: loadingStoreImages } = useQuery(storeImagesQuery, {
    variables: { id: pickpackStoreId },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setImages(
        assignIndices(
          data.store.images.map((image: Image) => ({ ...image, index: 0 })),
        ),
      );
      setDbImages(
        assignIndices(
          data.store.images.map((image: Image) => ({ ...image, index: 0 })),
        ),
      );
    },
  });

  const responsiveOptions = [
    {
      breakpoint: '1200px',
      numVisible: 3,
      numScroll: 1,
    },
    {
      breakpoint: '700px',
      numVisible: 2,
      numScroll: 1,
    },
    {
      breakpoint: '500px',
      numVisible: 1,
      numScroll: 1,
    },
  ];

  function assignIndices(images: ImageWithIndex[]) {
    for (let i = 0; i < images.length; i++) {
      images[i].index = i;
    }
    return images;
  }

  function swapImage(from: number, to: number) {
    const newImages = [...images];
    if (to >= 0 && to < newImages.length) {
      var tmp = newImages[from];
      tmp.index = to;
      newImages[from] = newImages[to];
      newImages[from].index = from;
      newImages[to] = tmp;
    }
    setImages(newImages);
    let changed = false;
    for (let i = 0; i < newImages.length && !changed; i++) {
      if (newImages[i] != dbImages[i]) {
        changed = true;
      }
    }
    setOrderChanged(changed);
  }

  function updateStoreImagesWrapper(newImages: ImageWithIndex[]) {
    setDbImages(newImages);
    setOrderChanged(false);
    updateStoreImagesMutation({
      variables: {
        id: pickpackStoreId,
        store: {
          images: newImages.map((i) => ({
            url: i.original_url || i.url,
            created_at: i.created_at || getCurrentDatePlusDays(0).toISOString(),
            source:
              i.source ||
              (allowedToAccess ? 'UPLOAD_BY_VYTAL' : 'UPLOAD_BY_STORE'),
            origin_url: i.origin_url,
          })),
        },
      },
      refetchQueries: [
        {
          query: storeImagesQuery,
          variables: {
            id: pickpackStoreId,
          },
        },
      ],
    }).catch((err) => {
      alert('Error updating store images: ' + err);
    });
  }

  function deleteImage(index: number) {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
    updateStoreImagesWrapper(newImages);
  }

  async function uploadStoreImage(e: any) {
    const { files } = e.target;
    const file = (files || [])[0];
    if (file) {
      const { name: filename, type: filetype } = file;
      setIsUploadingImage(true);
      const { data } = await requestUploadUrlQuery({
        variables: {
          filename: randomId(6) + '-' + filename.replace(/[^a-zA-Z ]/g, ''),
          filetype,
        },
      });
      if (data.requestUploadUrl) {
        const url = await uploadToS3(file, data.requestUploadUrl);
        if (url != null) {
          const newImages = [...images];
          newImages.push({ url, index: newImages.length });
          setImages(newImages);
          updateStoreImagesWrapper(newImages);
        } else {
          alert('error uploading image');
        }
      } else {
        alert('could not request upload url');
      }
    }
    setIsUploadingImage(false);
  }

  function openFileSelector() {
    const input = window.document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      uploadStoreImage(e);
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  }

  const imageTemplate = (image: ImageWithIndex) => {
    return (
      <div className="product-item">
        <div
          style={{
            textAlign: 'center',
            marginTop: '2rem',
            marginBottom: '1rem',
          }}
          className="product-item-content"
        >
          <div className="mb-3">
            <img
              style={{
                width: '80%',
                maxHeight: '250px',
                maxWidth: '250px',
                objectFit: 'contain',
              }}
              src={`${image.url}`}
              alt=""
            />
          </div>
          <div>
            <div className="car-buttons mt-5">
              <Button
                className="btn btn-secondary"
                onClick={() => swapImage(image.index, image.index - 1)}
              >
                <FaChevronLeft />
              </Button>
              <Button
                className="btn btn-danger"
                style={{ marginLeft: '0.3rem', marginRight: '0.3rem' }}
                onClick={() => deleteImage(image.index)}
              >
                <FaTrash />
              </Button>
              <Button
                className="btn btn-secondary"
                onClick={() => swapImage(image.index, image.index + 1)}
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isLoading =
    isUploadingImage || updatingStoreImages || loadingStoreImages;

  return (
    <div>
      <div
        className="card"
        style={{
          clear: 'both',
          backgroundColor: '#edf3f5',
          borderColor: 'grey',
        }}
      >
        <Carousel
          value={images}
          numVisible={3}
          numScroll={1}
          responsiveOptions={responsiveOptions}
          // circular
          itemTemplate={imageTemplate}
        />
      </div>
      <div className="float-end">
        <IconButton
          className="my-sm-2 mb-sm-2 my-2"
          icon={<FaUpload />}
          label={
            isLoading
              ? i18n.t('Common:loading')
              : i18n.t('StoreImage:uploadImage')
          }
          isLoading={isLoading}
          style={{ marginRight: '0.3rem' }}
          onClick={openFileSelector}
        />
        <IconButton
          className="my-sm-2 mb-sm-2 my-2"
          icon={<FaSave />}
          label={isLoading ? i18n.t('Common:loading') : i18n.t('Common:save')}
          isLoading={isLoading}
          disabled={!orderChanged}
          onClick={() => updateStoreImagesWrapper(images)}
        />
      </div>
    </div>
  );
};

type StoreImageViewProps = {
  pickpackStoreId: string | null;
};
